import { useCallback, useEffect, useState } from "react";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { http, formatApiError, downloadUrl } from "../lib/api";
import { recordLink } from "../lib/notify";
import { useNavDraft } from "../lib/navDraft";
import { fmtTL, fmtDate } from "../lib/format";
import { toast } from "../lib/notify";
import { Plus, Pencil, Trash2, Users, Wallet, FileDown, ExternalLink, ChevronDown, ChevronRight, Upload } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import CariForm from "../components/caris/CariForm";
import CariBulkUpload from "../components/caris/CariBulkUpload";
import ExportTemplateDialog from "../components/exports/ExportTemplateDialog";

const emptyForm = { name: "", contact: "", tax_office: "", tax_number: "", bank: "", iban: "", status: "Aktif", is_subcontractor: true };
const cls = "w-full px-3 py-2 text-sm border border-slate-300 rounded-md bg-white focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none";
// Bakiye rengi: borçlu kırmızı, alacaklı yeşil, sıfır gri
const balanceCls = (b) => {
  if (b > 0) return "text-red-700";
  return b < 0 ? "text-emerald-700" : "text-slate-500";
};
const balanceTag = (b) => {
  if (b >= 0) return b === 0 ? "" : "(Borçlu)";
  return "(Alacaklı)";
};
const TXN_TYPE_CLS = { "Gelir Faturası": "bg-emerald-100 text-emerald-800", "Gider Faturası": "bg-sky-100 text-sky-800" };
const txnTypeCls = (t) => TXN_TYPE_CLS[t] || "bg-amber-100 text-amber-800";

export default function Caris() {
  const { isAdmin } = useAuth();
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const returnTo = location.state?.from || "";
  const [caris, setCaris] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  useNavDraft("cari", () => (editing ? { editing, form } : null), (d) => { setEditing(d.editing); setForm(d.form); });
  const [selectedId, setSelectedIdState] = useState(params.get("cari") || "");
  const setSelectedId = (id) => { setSelectedIdState(id); const p = new URLSearchParams(params); if (id) p.set("cari", id); else p.delete("cari"); setParams(p, { replace: true }); };
  const [stmt, setStmt] = useState(null);
  const [stmtLoading, setStmtLoading] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = () => http.get("/caris/balances").then((r) => setCaris(r.data));
  useEffect(() => { load(); }, []);
  const [search, setSearch] = useState("");
  const filteredCaris = caris
    .filter((c) => !search || (c.name || "").toLocaleLowerCase("tr").includes(search.toLocaleLowerCase("tr")))
    .sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr"));

  useEffect(() => {
    if (params.get("yeni") === "1") {
      setForm({ ...emptyForm, name: (params.get("ad") || "").trim() });
      setEditing("new");
      const p = new URLSearchParams(params); p.delete("yeni"); p.delete("ad"); setParams(p, { replace: true, state: location.state });
    }
  }, [params, setParams, location.state]);

  // ?cari=<id>&duzenle=1 (ör. Ödeme Planı › IBAN Eksikleri): kart yüklenince satır açılır ve düzenleme formu hazır gelir
  useEffect(() => {
    if (params.get("duzenle") !== "1" || !caris.length) return;
    const c = caris.find((x) => x.id === params.get("cari"));
    const p = new URLSearchParams(params); p.delete("duzenle"); setParams(p, { replace: true, state: location.state });
    if (c) { setForm({ ...emptyForm, ...c }); setEditing(c.id); setSelectedIdState(c.id); setTimeout(() => document.querySelector(`[data-testid="cari-row-${c.id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 150); }
  }, [params, caris, setParams, location.state]);

  const loadStatement = useCallback((id) => {
    if (!id) { setStmt(null); return; }
    setStmtLoading(true);
    http.get(`/caris/${id}/statement`)
      .then((r) => setStmt(r.data))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setStmtLoading(false));
  }, []);
  useEffect(() => { loadStatement(selectedId); }, [selectedId, loadStatement]);

  const selected = caris.find((c) => c.id === selectedId);

  const startNew = () => { setForm(emptyForm); setEditing("new"); };
  const startEdit = (c) => { setForm({ ...emptyForm, ...c }); setEditing(c.id); setSelectedId(c.id); };
  const cancel = () => { setEditing(null); setForm(emptyForm); };

  // ESC: form açıkken CariForm kendisi kapatır; değilse geldiği sayfaya dön
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape" || editing || e.target.closest("input,textarea,select")) return;
      if (returnTo) navigate(returnTo, { replace: true });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editing, returnTo, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      let newId = null;
      if (editing === "new") newId = (await http.post("/caris", form)).data?.id;
      else await http.put(`/caris/${editing}`, form);
      toast.success("Cari kaydedildi", { link: recordLink(editing === "new" ? newId : editing) });
      cancel(); await load();
      if (editing === "new" && returnTo) navigate(returnTo, { state: { newCari: form.name } });
      else if (editing === "new" && newId) setSelectedId(newId);
      else if (editing && editing !== "new") loadStatement(editing);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Cari silinsin mi?")) return;
    try {
      await http.delete(`/caris/${id}`);
      toast.success("Silindi");
      if (selectedId === id) { setSelectedId(""); setStmt(null); }
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6" data-testid="page-caris">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Users className="w-7 h-7 text-amber-500" /> Cari Listesi
          </h1>
          <p className="text-sm text-slate-500 mt-1">Müşteri, taşeron ve tedarikçiler tek listede. Cari seçin — fatura ve çek hareketleri borç/alacak/bakiye olarak listelenir.</p>
        </div>
        {!editing && (
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <button type="button" onClick={() => setExportOpen(true)} data-testid="cari-export-open" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-emerald-700 bg-emerald-50 text-emerald-800 text-sm font-semibold hover:bg-emerald-100">
              <FileDown className="w-4 h-4" /> Excel'e Aktar
            </button>
            <button type="button" onClick={() => setBulkOpen((v) => !v)} data-testid="cari-bulk-open" className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md border text-sm font-semibold ${bulkOpen ? "border-amber-500 bg-amber-100 text-amber-900" : "border-amber-700 bg-amber-50 text-amber-900 hover:bg-amber-100"}`}>
              <Upload className="w-4 h-4" /> Excel'den Yükle
            </button>
            <button onClick={startNew} data-testid="cari-add" className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800">
              <Plus className="w-4 h-4" /> Yeni Cari
            </button>
          </div>
        )}
      </div>

      <ExportTemplateDialog open={exportOpen} onClose={() => setExportOpen(false)} kind="caris" title="Cari Listesi — Excel'e Aktar" endpoint="/caris/export" filename="cari-listesi" testid="cexp" />
      {bulkOpen && <CariBulkUpload onSaved={load} onClose={() => setBulkOpen(false)} />}

      {editing === "new" && <CariForm form={form} setForm={setForm} onSubmit={submit} onCancel={cancel} />}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="cari-table-wrap">
        <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-center gap-3">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari ara…" data-testid="cari-search" className={`${cls} max-w-xs`} />
          <span className="text-xs text-slate-500" data-testid="cari-count">{filteredCaris.length} cari</span>
          {caris.length === 0 && <span className="text-[11px] text-amber-700">Liste boş — "Yeni Cari" ile ekleyin</span>}
        </div>
        <table className="w-full tbl">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600 font-semibold text-left w-8"></th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600 font-semibold text-left">Cari</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600 font-semibold text-left">Durum</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600 font-semibold text-right">Borç</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600 font-semibold text-right">Alacak</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600 font-semibold text-right">Bakiye</th>
            </tr>
          </thead>
          <tbody>
            {filteredCaris.map((c) => {
              const open = c.id === selectedId;
              return [
                <tr key={c.id} onClick={() => setSelectedId(open ? "" : c.id)} data-testid={`cari-row-${c.id}`}
                  className={`border-t border-slate-100 cursor-pointer transition-colors ${open ? "bg-amber-50" : "hover:bg-slate-50"}`}>
                  <td className="px-3 py-2 text-slate-400">{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</td>
                  <td className="px-3 py-2 text-xs font-semibold text-slate-900">{c.name}</td>
                  <td className="px-3 py-2 text-xs"><span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${c.status === "Aktif" ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`}>{c.status}</span></td>
                  <td className="numeric-cell px-3 py-2 text-xs font-semibold text-red-700">{c.total_borc ? fmtTL(c.total_borc) : "—"}</td>
                  <td className="numeric-cell px-3 py-2 text-xs font-semibold text-emerald-700">{c.total_alacak ? fmtTL(c.total_alacak) : "—"}</td>
                  <td className={`numeric-cell px-3 py-2 text-xs font-bold ${balanceCls(c.balance)}`} data-testid={`cari-balance-${c.id}`}>
                    {fmtTL(c.balance || 0)}{c.balance ? <span className="text-slate-400 font-normal text-[10px]"> {c.balance > 0 ? "(B)" : "(A)"}</span> : null}
                  </td>
                </tr>,
                open && selected && (
                  <tr key={`${c.id}-detail`} className="bg-amber-50/30">
                    <td colSpan={6} className="px-4 pb-4 pt-2 space-y-4">
                      <div className="flex items-start justify-between gap-4" data-testid="cari-detail">
                        <div className="text-xs text-slate-600 space-y-1">
                          <div>{[selected.contact, selected.tax_office && `V.D: ${selected.tax_office}`, selected.tax_number && `V.No: ${selected.tax_number}`].filter(Boolean).join(" · ") || "İletişim bilgisi yok"}</div>
                          <div>{[selected.bank, selected.iban].filter(Boolean).join(" · ")}</div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button onClick={() => (editing === selected.id ? cancel() : startEdit(selected))} className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border text-xs font-semibold bg-white ${editing === selected.id ? "border-orange-400 text-orange-700" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`} title={editing === selected.id ? "Düzenleme açık — ESC ile kapat" : "Satırın altında düzenle"} data-testid="cari-edit"><Pencil className="w-3.5 h-3.5" /> {editing === selected.id ? "Düzenlemeyi Kapat" : "Düzenle"}</button>
                          {isAdmin && (
                            <button onClick={() => remove(selected.id)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-red-300 text-xs font-semibold text-red-700 hover:bg-red-50 bg-white" data-testid="cari-delete"><Trash2 className="w-3.5 h-3.5" /> Sil</button>
                          )}
                        </div>
                      </div>
                      {editing === selected.id && <CariForm form={form} setForm={setForm} onSubmit={submit} onCancel={cancel} inline />}
                      <div className="border border-slate-200 rounded-lg overflow-hidden bg-white" data-testid="cari-statement">
            <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center gap-2 text-xs font-bold text-slate-700 uppercase tracking-wider">
              <Wallet className="w-4 h-4 text-amber-500" /> Cari Hareketler
              {stmt && (
                <a href={downloadUrl(`/caris/${selectedId}/statement/excel`)} target="_blank" rel="noreferrer" data-testid="cari-stmt-excel"
                  className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded border border-emerald-600 bg-emerald-50 text-[11px] font-semibold text-emerald-800 hover:bg-emerald-100 normal-case tracking-normal transition-colors">
                  <FileDown className="w-3.5 h-3.5" /> Excel İndir
                </a>
              )}
              {stmt && (
                <span className="normal-case tracking-normal font-semibold" data-testid="cari-stmt-balance">
                  Bakiye: <span className={stmt.balance >= 0 ? "text-red-700" : "text-emerald-700"}>{fmtTL(stmt.balance)}</span>
                  <span className="text-slate-400 font-normal"> {balanceTag(stmt.balance)}</span>
                </span>
              )}
            </div>
            <table className="w-full tbl">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600 font-semibold text-left">Tarih</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600 font-semibold text-left">İşlem Türü</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600 font-semibold text-left">Belge No</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600 font-semibold text-left">Açıklama</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600 font-semibold text-right">Borç</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600 font-semibold text-right">Alacak</th>
                  <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600 font-semibold text-right">Bakiye</th>
                </tr>
              </thead>
              <tbody>
                {stmtLoading && <tr><td colSpan={7} className="py-8 text-center text-sm text-slate-500" data-testid="cari-stmt-loading">Yükleniyor…</td></tr>}
                {!stmtLoading && stmt && stmt.transactions.map((t, i) => (
                  <tr key={t.id || `${t.date}-${t.doc_no || ""}-${i}`} className="border-t border-slate-100" data-testid={`cari-stmt-row-${i}`}>
                    <td className="px-3 py-2 text-xs whitespace-nowrap">{t.date ? fmtDate(t.date) : "—"}</td>
                    <td className="px-3 py-2 text-xs">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${txnTypeCls(t.type)}`}>{t.type}</span>
                    </td>
                    <td className="px-3 py-2 text-xs font-mono">
                      {t.ref_id ? (
                        <button data-testid={`cari-stmt-goto-${i}`} title={t.ref_type === "check" ? "Çek kaydına git (ESC ile geri dön)" : "Fatura kaydına git (ESC ile geri dön)"}
                          onClick={() => navigate(t.ref_type === "check" ? `/veri-girisi?bolum=cek&kayit=${t.ref_id}` : `/veri-girisi?bolum=fatura&fatura=${t.ref_id}&don=${encodeURIComponent(window.location.pathname + window.location.search)}`)}
                          className="inline-flex items-center gap-1 text-amber-800 hover:text-amber-950 hover:underline underline-offset-2 transition-colors">
                          {t.doc_no || "Kayda git"} <ExternalLink className="w-3 h-3 opacity-60" />
                        </button>
                      ) : (t.doc_no || "—")}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">{t.description || "—"}</td>
                    <td className="numeric-cell px-3 py-2 text-xs font-semibold text-red-700">{t.borc ? fmtTL(t.borc) : "—"}</td>
                    <td className="numeric-cell px-3 py-2 text-xs font-semibold text-emerald-700">{t.alacak ? fmtTL(t.alacak) : "—"}</td>
                    <td className="numeric-cell px-3 py-2 text-xs font-bold text-slate-900" data-testid={`cari-stmt-balance-${i}`}>{fmtTL(t.balance)}</td>
                  </tr>
                ))}
                {!stmtLoading && stmt && stmt.transactions.length === 0 && (
                  <tr><td colSpan={7} className="py-8 text-center text-sm text-slate-500" data-testid="cari-stmt-empty">Bu cari için fatura veya çek hareketi yok</td></tr>
                )}
                {!stmtLoading && !stmt && <tr><td colSpan={7} className="py-8 text-center text-sm text-slate-500">—</td></tr>}
              </tbody>
              {stmt && stmt.transactions.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-amber-300 bg-amber-50/60" data-testid="cari-stmt-totals">
                    <td colSpan={4} className="px-3 py-2 text-xs font-bold text-amber-900">TOPLAM</td>
                    <td className="numeric-cell px-3 py-2 text-xs font-bold text-red-700" data-testid="cari-stmt-total-borc">{fmtTL(stmt.total_borc)}</td>
                    <td className="numeric-cell px-3 py-2 text-xs font-bold text-emerald-700" data-testid="cari-stmt-total-alacak">{fmtTL(stmt.total_alacak)}</td>
                    <td className="numeric-cell px-3 py-2 text-xs font-bold text-slate-900" data-testid="cari-stmt-total-balance">{fmtTL(stmt.balance)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
                      </div>
                    </td>
                  </tr>
                ),
              ];
            })}
            {filteredCaris.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-sm text-slate-500" data-testid="cari-stmt-hint">Kayıt yok</td></tr>}
          </tbody>
          {filteredCaris.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-slate-300 bg-slate-50" data-testid="cari-list-totals">
                <td colSpan={3} className="px-3 py-2 text-xs font-bold text-slate-900">TOPLAM</td>
                <td className="numeric-cell px-3 py-2 text-xs font-bold text-red-700">{fmtTL(filteredCaris.reduce((s, c) => s + (c.total_borc || 0), 0))}</td>
                <td className="numeric-cell px-3 py-2 text-xs font-bold text-emerald-700">{fmtTL(filteredCaris.reduce((s, c) => s + (c.total_alacak || 0), 0))}</td>
                <td className="numeric-cell px-3 py-2 text-xs font-bold text-slate-900">{fmtTL(filteredCaris.reduce((s, c) => s + (c.balance || 0), 0))}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
